import { readTranslatedPage } from '$lib/apis/read/api'

type CacheKey = string

const blobCache = new Map<CacheKey, Blob>()
const inflight = new Map<CacheKey, Promise<Blob | null>>()

/** Max concurrent translation API calls across the reader. */
const MAX_CONCURRENT = 2
let activeCount = 0
const waitQueue: Array<() => void> = []

function cacheKey(bookId: string, page: number, force: boolean) {
  return `${bookId}:${page}:${force ? '1' : '0'}`
}

function acquireSlot(): Promise<void> {
  if (activeCount < MAX_CONCURRENT) {
    activeCount++
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    waitQueue.push(() => {
      activeCount++
      resolve()
    })
  })
}

function releaseSlot() {
  activeCount--
  const next = waitQueue.shift()
  if (next) next()
}

export function getCachedTranslation(
  bookId: string,
  page: number,
  force: boolean,
): Blob | undefined {
  if (force) return undefined
  return blobCache.get(cacheKey(bookId, page, false))
}

export function clearTranslationCache() {
  blobCache.clear()
  inflight.clear()
}

export async function fetchTranslation(
  server: any,
  bookId: string,
  page: number,
  force: boolean,
  signal?: AbortSignal,
): Promise<Blob | null> {
  const key = cacheKey(bookId, page, force)

  if (!force) {
    const cached = blobCache.get(key)
    if (cached) return cached
  }

  const existing = inflight.get(key)
  if (existing) {
    try {
      return await existing
    } catch {
      return null
    }
  }

  const run = (async () => {
    await acquireSlot()
    try {
      if (signal?.aborted) return null
      const blob = await readTranslatedPage(server, bookId, page, force, signal)
      if (blob) {
        // Always refresh the normal cache entry so force results stick.
        blobCache.set(cacheKey(bookId, page, false), blob)
      }
      return blob
    } finally {
      releaseSlot()
      inflight.delete(key)
    }
  })()

  inflight.set(key, run)
  return run
}
